/**
 * Message Actions Component
 *
 * Displays action buttons for version control on individual messages:
 * - Revert to this point (delete all messages after)
 * - Revert with Draft (delete messages after and pre-populate composer with AI response)
 * - Fork from this point (create new thread up to this message)
 */

"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState } from "react";
import RevertOptionsModal from "./RevertOptionsModal";
import RevertWithDraftModal from "./RevertWithDraftModal";
import type { AIModel } from "@/lib/apiKeyStorage";
import type { FeatureId } from "@/types/features";
type MessageActionsProps = {
  messageId: string;
  messageModel: string | null;
  isLastMessage: boolean;
  isUserMessage: boolean;
  currentModel: AIModel;
  messagesAfterCount: number;
  onRevert: (messageId: string, switchToOriginalModel: boolean) => void;
  onRevertWithDraft: (messageId: string, switchToOriginalModel: boolean) => void;
  onForkFrom: (messageId: string) => void;
  hasAiResponseAfter: boolean;
  disabled?: boolean;
  isFeatureEnabled?: (featureId: FeatureId) => boolean;
};
const MessageActions: React.FC<MessageActionsProps> = t0 => {
  const $ = _c(56);
  const {
    messageId,
    messageModel,
    isLastMessage,
    isUserMessage,
    currentModel,
    messagesAfterCount,
    onRevert,
    onRevertWithDraft,
    onForkFrom,
    disabled: t1,
    isFeatureEnabled
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [showRevertWithDraftModal, setShowRevertWithDraftModal] = useState(false);
  let t2;
  if ($[0] !== isFeatureEnabled) {
    t2 = isFeatureEnabled ? isFeatureEnabled("revert") : true;
    $[0] = isFeatureEnabled;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const revertEnabled = t2;
  let t3;
  if ($[2] !== isFeatureEnabled) {
    t3 = isFeatureEnabled ? isFeatureEnabled("revert_with_draft") : true;
    $[2] = isFeatureEnabled;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const revertWithDraftEnabled = t3;
  let t4;
  if ($[4] !== isFeatureEnabled) {
    t4 = isFeatureEnabled ? isFeatureEnabled("fork_thread") : true;
    $[4] = isFeatureEnabled;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const forkEnabled = t4;
  const showRevert = revertEnabled && !isUserMessage && !isLastMessage;
  const showRevertWithDraft = revertWithDraftEnabled && !isUserMessage && !isLastMessage && messagesAfterCount > 0;
  const showFork = forkEnabled && !isUserMessage;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setShowRevertModal(true);
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const handleRevertClick = t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setShowRevertWithDraftModal(true);
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const handleRevertWithDraftClick = t6;
  let t7;
  if ($[8] !== messageId || $[9] !== onRevert) {
    t7 = () => {
      onRevert(messageId, false);
    };
    $[8] = messageId;
    $[9] = onRevert;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const handleRevertWithCurrentModel = t7;
  let t8;
  if ($[11] !== messageId || $[12] !== onRevert) {
    t8 = () => {
      onRevert(messageId, true);
    };
    $[11] = messageId;
    $[12] = onRevert;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const handleRevertWithOriginalModel = t8;
  let t9;
  if ($[14] !== messageId || $[15] !== onRevertWithDraft) {
    t9 = () => {
      onRevertWithDraft(messageId, false);
    };
    $[14] = messageId;
    $[15] = onRevertWithDraft;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const handleRevertWithDraftCurrentModel = t9;
  let t10;
  if ($[17] !== messageId || $[18] !== onRevertWithDraft) {
    t10 = () => {
      onRevertWithDraft(messageId, true);
    };
    $[17] = messageId;
    $[18] = onRevertWithDraft;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const handleRevertWithDraftOriginalModel = t10;
  const t11 = `flex gap-1.5 mb-1 ${isUserMessage ? "justify-end" : "justify-start"}`;
  let t12;
  if ($[20] !== disabled || $[21] !== messageId || $[22] !== onForkFrom || $[23] !== showFork) {
    t12 = showFork && <button onClick={() => onForkFrom(messageId)} disabled={disabled} className="rounded-md border border-foreground/30 bg-white/60 px-2 py-1 text-[10px] font-medium text-foreground hover:bg-white/80 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1" title="Fork thread from this message - create a new thread with messages up to this point"><svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>Fork</button>;
    $[20] = disabled;
    $[21] = messageId;
    $[22] = onForkFrom;
    $[23] = showFork;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== disabled || $[26] !== showRevert) {
    t13 = showRevert && <button onClick={handleRevertClick} disabled={disabled} className="rounded-md border border-foreground/30 bg-white/60 px-2 py-1 text-[10px] font-medium text-foreground hover:bg-white/80 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1" title="Revert to this message - delete all messages after this point"><svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" /></svg>Revert</button>;
    $[25] = disabled;
    $[26] = showRevert;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== disabled || $[29] !== showRevertWithDraft) {
    t14 = showRevertWithDraft && <button onClick={handleRevertWithDraftClick} disabled={disabled} className="rounded-md border border-foreground/30 bg-white/60 px-2 py-1 text-[10px] font-medium text-foreground hover:bg-white/80 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1" title="Revert and pre-populate message composer with the AI response as a draft"><svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>Revert + Draft</button>;
    $[28] = disabled;
    $[29] = showRevertWithDraft;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t11 || $[32] !== t12 || $[33] !== t13 || $[34] !== t14) {
    t15 = <div className={t11}>{t12}{t13}{t14}</div>;
    $[31] = t11;
    $[32] = t12;
    $[33] = t13;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => setShowRevertModal(false);
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== currentModel || $[38] !== handleRevertWithCurrentModel || $[39] !== handleRevertWithOriginalModel || $[40] !== messageModel || $[41] !== messagesAfterCount || $[42] !== showRevertModal) {
    t17 = <RevertOptionsModal isOpen={showRevertModal} onClose={t16} onRevertWithCurrentModel={handleRevertWithCurrentModel} onRevertWithOriginalModel={handleRevertWithOriginalModel} currentModel={currentModel} originalModel={messageModel} messagesCount={messagesAfterCount} />;
    $[37] = currentModel;
    $[38] = handleRevertWithCurrentModel;
    $[39] = handleRevertWithOriginalModel;
    $[40] = messageModel;
    $[41] = messagesAfterCount;
    $[42] = showRevertModal;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => setShowRevertWithDraftModal(false);
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== currentModel || $[46] !== handleRevertWithDraftCurrentModel || $[47] !== handleRevertWithDraftOriginalModel || $[48] !== messageModel || $[49] !== messagesAfterCount || $[50] !== showRevertWithDraftModal) {
    t19 = <RevertWithDraftModal isOpen={showRevertWithDraftModal} onClose={t18} onRevertWithCurrentModel={handleRevertWithDraftCurrentModel} onRevertWithOriginalModel={handleRevertWithDraftOriginalModel} currentModel={currentModel} originalModel={messageModel} messagesCount={messagesAfterCount} />;
    $[45] = currentModel;
    $[46] = handleRevertWithDraftCurrentModel;
    $[47] = handleRevertWithDraftOriginalModel;
    $[48] = messageModel;
    $[49] = messagesAfterCount;
    $[50] = showRevertWithDraftModal;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] !== t15 || $[53] !== t17 || $[54] !== t19) {
    t20 = <>{t15}{t17}{t19}</>;
    $[52] = t15;
    $[53] = t17;
    $[54] = t19;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  return t20;
};
export default MessageActions;